import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import ExcelJS from 'exceljs';
import { IconPlus, IconCheck, IconGrid, IconEdit, IconTrash, IconSearch } from './Icons';
import './Clients.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const clientsSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;

const emptyForm = () => ({
  name: '',
  label: '',
  email: '',
  phone: '',
  company_name: '',
  address: '',
  notes: '',
  primary_project_id: '',
});

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [projects, setProjects] = useState([]);
  const mountedRef = useRef(true);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      clientsSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchClients = useCallback(async (mountedRef) => {
    const isMounted = () => !mountedRef || mountedRef.current;
    setLoading(true);
    setError(null);
    try {
      const fetchPromise = clientsSupabase.from('clients').select('*').order('created_at', { ascending: false });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const result = await Promise.race([fetchPromise, timeoutPromise]);
      if (!isMounted()) return;
      const { data, err } = result;
      if (err) {
        setError(err.message);
        setClients([]);
      } else {
        setClients(data ?? []);
      }
    } catch (e) {
      if (isMounted()) {
        setError(e?.message || 'Failed to load clients.');
        setClients([]);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, []);

  const fetchProjects = useCallback(async (mountedRef) => {
    const isMounted = () => !mountedRef || mountedRef.current;
    const { data, err } = await clientsSupabase
      .from('projects')
      .select('id, title, status')
      .order('title', { ascending: true, nullsFirst: false });
    if (!isMounted()) return;
    if (err) {
      setProjects([]);
    } else {
      setProjects(data ?? []);
    }
  }, []);

  const location = useLocation();

  const refreshSessionAndFetch = useCallback(async () => {
    mountedRef.current = true;
    setError(null);
    try {
      await clientsSupabase.auth.getSession();
      await runWakeUp();
      await fetchClients(mountedRef);
      if (!mountedRef.current) return;
      await fetchProjects(mountedRef);
    } catch (e) {
      setError(e?.message || 'Failed to load');
    }
  }, [runWakeUp, fetchClients, fetchProjects]);

  useEffect(() => {
    if (location.pathname !== '/clients') return;
    let cancelled = false;
    mountedRef.current = true;
    (async () => {
      const { error: sessionErr } = await clientsSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setError(sessionErr.message);
        setLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      await fetchClients(mountedRef);
      if (cancelled) return;
      await fetchProjects(mountedRef);
    })();
    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, [location.pathname, runWakeUp, fetchClients, fetchProjects]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  const projectsById = React.useMemo(
    () => (projects || []).reduce((acc, p) => ({ ...acc, [p.id]: p }), {}),
    [projects],
  );

  const projectStats = React.useMemo(() => {
    let clientsWithOpen = 0;
    let clientsWithCompleted = 0;
    const completedStatuses = ['closed', 'completed', 'done'];
    clients.forEach((c) => {
      const proj = c.primary_project_id ? projectsById[c.primary_project_id] : null;
      if (!proj) return;
      const status = (proj.status || '').toLowerCase();
      if (status === 'open') clientsWithOpen += 1;
      else if (completedStatuses.includes(status)) clientsWithCompleted += 1;
    });
    return { clientsWithOpen, clientsWithCompleted };
  }, [clients, projectsById]);

  const filteredClients = clients.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const name = (c.name ?? '').toLowerCase();
    const label = (c.label ?? '').toLowerCase();
    const email = (c.email ?? '').toLowerCase();
    const phone = (c.phone ?? '').toLowerCase();
    const company = (c.company_name ?? '').toLowerCase();
    return (
      name.includes(q) ||
      label.includes(q) ||
      email.includes(q) ||
      phone.includes(q) ||
      company.includes(q)
    );
  });

  const handleExportExcel = async () => {
    if (!filteredClients.length || exporting) return;
    setExporting(true);
    setError(null);

    const EXPORT_COLUMNS = [
      { key: 'name', label: 'Name', width: 24 },
      { key: 'company_name', label: 'Company', width: 28 },
      { key: 'email', label: 'Email', width: 30 },
      { key: 'phone', label: 'Phone', width: 18 },
      { key: 'label', label: 'Label', width: 18 },
      { key: 'project', label: 'Project', width: 28 },
      { key: 'created', label: 'Created', width: 14 },
    ];
    const borderThin = {
      top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
    };
    const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB45309' } };
    const zebraFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF7ED' } };

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'ECDT Portal';
      workbook.created = new Date();
      const sheet = workbook.addWorksheet('Clients', {
        properties: { tabColor: { argb: 'FFB45309' } },
      });

      try {
        const logoUrl = `${process.env.PUBLIC_URL || ''}/logo.png`;
        const res = await fetch(logoUrl);
        if (res.ok) {
          const blob = await res.blob();
          const arrayBuffer = await blob.arrayBuffer();
          const imageId = workbook.addImage({ buffer: arrayBuffer, extension: 'png' });
          sheet.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 180, height: 48 } });
        }
      } catch (e) {
        console.warn('[Clients] Excel logo fetch failed', e);
      }

      const titleRowIndex = 4;
      const titleRow = sheet.getRow(titleRowIndex);
      titleRow.getCell(1).value = 'Clients';
      titleRow.height = 22;
      titleRow.font = { bold: true, size: 16, color: { argb: 'FFB45309' }, name: 'Calibri' };
      sheet.mergeCells(titleRowIndex, 1, titleRowIndex, EXPORT_COLUMNS.length);

      const subRowIndex = titleRowIndex + 1;
      const subRow = sheet.getRow(subRowIndex);
      subRow.getCell(1).value = `ECDT Portal · Exported ${new Date().toLocaleString()}`;
      subRow.font = { size: 10, color: { argb: 'FF6B7280' }, name: 'Calibri' };
      sheet.mergeCells(subRowIndex, 1, subRowIndex, EXPORT_COLUMNS.length);

      const headerRowIndex = subRowIndex + 2;
      const headerRow = sheet.getRow(headerRowIndex);
      EXPORT_COLUMNS.forEach((col, idx) => {
        headerRow.getCell(idx + 1).value = col.label;
      });
      headerRow.height = 20;
      headerRow.eachCell({ includeEmpty: false }, (cell) => {
        cell.fill = headerFill;
        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true, name: 'Calibri', size: 11 };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
        cell.border = borderThin;
      });

      let rowIndex = headerRowIndex + 1;
      filteredClients.forEach((c, i) => {
        const row = sheet.getRow(rowIndex);
        const values = {
          name: c.name ?? '',
          company_name: c.company_name ?? '',
          email: c.email ?? '',
          phone: c.phone ?? '',
          label: c.label ?? '',
          project: c.primary_project_id && projectsById[c.primary_project_id] ? projectsById[c.primary_project_id].title : '',
          created: formatDate(c.created_at),
        };
        EXPORT_COLUMNS.forEach((col, idx) => {
          const cell = row.getCell(idx + 1);
          cell.value = values[col.key] ?? '';
          cell.font = { name: 'Calibri', size: 11, color: { argb: 'FF1F2937' } };
          cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
          cell.border = borderThin;
          if (i % 2 === 1) {
            cell.fill = zebraFill;
          }
        });
        row.height = 18;
        rowIndex += 1;
      });

      sheet.columns = EXPORT_COLUMNS.map((c) => ({ width: c.width }));

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clients-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[Clients] export failed', e);
      setError(e?.message || 'Export failed. Try again.');
    } finally {
      setExporting(false);
    }
  };

  const openAdd = () => {
    setSelected(null);
    setForm(emptyForm());
    setShowAddModal(true);
  };

  const openEdit = (client) => {
    setSelected(client);
    setForm({
      name: client.name ?? '',
      label: client.label ?? '',
      email: client.email ?? '',
      phone: client.phone ?? '',
      company_name: client.company_name ?? '',
      address: client.address ?? '',
      notes: client.notes ?? '',
      primary_project_id: client.primary_project_id ?? '',
    });
    setShowEditModal(true);
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setSelected(null);
    setForm(emptyForm());
    setDeleteConfirm(null);
  };

  const payload = (isEdit) => ({
    name: form.name.trim(),
    label: form.label.trim() || null,
    email: form.email.trim() || null,
    phone: form.phone.trim() || null,
    company_name: form.company_name.trim() || null,
    address: form.address.trim() || null,
    notes: form.notes.trim() || null,
    primary_project_id: form.primary_project_id?.trim() || null,
    ...(isEdit ? { updated_at: new Date().toISOString() } : {}),
  });

  const handleSaveNew = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await runWakeUp();
      const { error: err } = await clientsSupabase.from('clients').insert(payload(false));
      if (err) {
        setError(err.message);
        setSaving(false);
        return;
      }
      closeModals();
      fetchClients(mountedRef);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selected || !form.name.trim()) return;
    setSaving(true);
    try {
      await runWakeUp();
      const { error: err } = await clientsSupabase
        .from('clients')
        .update(payload(true))
        .eq('id', selected.id);
      if (err) {
        setError(err.message);
        setSaving(false);
        return;
      }
      closeModals();
      fetchClients(mountedRef);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (client) => {
    setDeleteConfirm(client);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    try {
      await runWakeUp();
      const { error: err } = await clientsSupabase.from('clients').delete().eq('id', deleteConfirm.id);
      if (err) {
        setError(err.message);
        setDeleteConfirm(null);
        return;
      }
      closeModals();
      fetchClients(mountedRef);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="clients-page">
      <div className="clients-header">
        <h1 className="clients-title">Clients</h1>
      </div>

      <div className="clients-actions">
        <button type="button" className="clients-action-btn clients-action-primary" onClick={openAdd}>
          <IconPlus />
          Add client
        </button>
      </div>

      {error && (
        <div className="clients-error" role="alert">
          {error}
        </div>
      )}

      <section className="clients-section clients-section-projects-status">
        <h2 className="clients-section-title">Projects Status</h2>
        <ul className="clients-status-list clients-status-with-icons">
          <li>
            <span className="clients-status-icon"><IconGrid /></span>
            Clients with open projects: <strong>{loading ? '…' : projectStats.clientsWithOpen}</strong>
          </li>
          <li>
            <span className="clients-status-icon"><IconCheck /></span>
            Clients with completed projects: <strong>{loading ? '…' : projectStats.clientsWithCompleted}</strong>
          </li>
        </ul>
      </section>

      <div className="clients-list-wrap">
          <div className="clients-toolbar">
            <button
              type="button"
              className="clients-toolbar-btn"
              onClick={handleExportExcel}
              disabled={loading || filteredClients.length === 0 || exporting}
            >
              {exporting ? 'Exporting…' : 'Export Excel'}
            </button>
            <div className="clients-search-wrap">
              <span className="clients-search-icon"><IconSearch /></span>
              <input
                type="search"
                className="clients-search"
                placeholder="Search clients…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search clients"
              />
            </div>
          </div>
          {loading ? (
            <div className="clients-loading">Loading clients…</div>
          ) : (
            <div className="clients-table-wrap">
              <table className="clients-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Company</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Label</th>
                    <th>Project</th>
                    <th>Created</th>
                    <th className="clients-th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="clients-empty">No clients yet. Add one with the button above.</td>
                    </tr>
                  ) : (
                    filteredClients.map((c) => (
                      <tr key={c.id}>
                        <td>{c.name ?? '—'}</td>
                        <td>{c.company_name ?? '—'}</td>
                        <td>{c.email ?? '—'}</td>
                        <td>{c.phone ?? '—'}</td>
                        <td>{c.label ?? '—'}</td>
                        <td>{c.primary_project_id && projectsById[c.primary_project_id] ? projectsById[c.primary_project_id].title : '—'}</td>
                        <td>{formatDate(c.created_at)}</td>
                        <td className="clients-actions-cell">
                          <button type="button" className="clients-row-btn" onClick={() => openEdit(c)} title="Edit">
                            <IconEdit />
                          </button>
                          <button type="button" className="clients-row-btn clients-row-btn-danger" onClick={() => handleDelete(c)} title="Delete">
                            <IconTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {showAddModal && (
        <div className="clients-modal-backdrop" onClick={closeModals}>
          <div className="clients-modal clients-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h2 className="clients-modal-title">Add client</h2>
            <form onSubmit={handleSaveNew}>
              <div className="clients-form-row">
                <div className="clients-form-group">
                  <label htmlFor="add-name">Name</label>
                  <input
                    id="add-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Client or contact name"
                    autoFocus
                    required
                  />
                </div>
                <div className="clients-form-group">
                  <label htmlFor="add-company">Company name</label>
                  <input
                    id="add-company"
                    type="text"
                    value={form.company_name}
                    onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="clients-form-row">
                <div className="clients-form-group">
                  <label htmlFor="add-email">Email</label>
                  <input
                    id="add-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="clients-form-group">
                  <label htmlFor="add-phone">Phone</label>
                  <input
                    id="add-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="clients-form-group">
                <label htmlFor="add-label">Label</label>
                <input
                  id="add-label"
                  type="text"
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="Optional label"
                />
              </div>
              <div className="clients-form-group">
                <label htmlFor="add-project">Primary project</label>
                <select
                  id="add-project"
                  value={form.primary_project_id}
                  onChange={(e) => setForm((f) => ({ ...f, primary_project_id: e.target.value }))}
                  className="clients-form-select"
                >
                  <option value="">None</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.title || 'Untitled'}</option>
                  ))}
                </select>
              </div>
              <div className="clients-form-group">
                <label htmlFor="add-address">Address</label>
                <input
                  id="add-address"
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
              <div className="clients-form-group">
                <label htmlFor="add-notes">Notes</label>
                <textarea
                  id="add-notes"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Internal notes"
                  rows={3}
                  className="clients-form-textarea"
                />
              </div>
              <div className="clients-modal-actions">
                <button type="button" className="clients-btn-secondary" onClick={closeModals}>Cancel</button>
                <button type="submit" className="clients-btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Add client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && selected && (
        <div className="clients-modal-backdrop" onClick={closeModals}>
          <div className="clients-modal clients-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h2 className="clients-modal-title">Edit client</h2>
            <form onSubmit={handleSaveEdit}>
              <div className="clients-form-row">
                <div className="clients-form-group">
                  <label htmlFor="edit-name">Name</label>
                  <input
                    id="edit-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Client or contact name"
                    required
                  />
                </div>
                <div className="clients-form-group">
                  <label htmlFor="edit-company">Company name</label>
                  <input
                    id="edit-company"
                    type="text"
                    value={form.company_name}
                    onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="clients-form-row">
                <div className="clients-form-group">
                  <label htmlFor="edit-email">Email</label>
                  <input
                    id="edit-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="clients-form-group">
                  <label htmlFor="edit-phone">Phone</label>
                  <input
                    id="edit-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="clients-form-group">
                <label htmlFor="edit-label">Label</label>
                <input
                  id="edit-label"
                  type="text"
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="Optional label"
                />
              </div>
              <div className="clients-form-group">
                <label htmlFor="edit-project">Primary project</label>
                <select
                  id="edit-project"
                  value={form.primary_project_id}
                  onChange={(e) => setForm((f) => ({ ...f, primary_project_id: e.target.value }))}
                  className="clients-form-select"
                >
                  <option value="">None</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.title || 'Untitled'}</option>
                  ))}
                </select>
              </div>
              <div className="clients-form-group">
                <label htmlFor="edit-address">Address</label>
                <input
                  id="edit-address"
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
              <div className="clients-form-group">
                <label htmlFor="edit-notes">Notes</label>
                <textarea
                  id="edit-notes"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Internal notes"
                  rows={3}
                  className="clients-form-textarea"
                />
              </div>
              <div className="clients-modal-actions">
                <button type="button" className="clients-btn-secondary" onClick={closeModals}>Cancel</button>
                <button type="submit" className="clients-btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="clients-modal-backdrop" onClick={() => setDeleteConfirm(null)}>
          <div className="clients-modal clients-modal-confirm" onClick={(e) => e.stopPropagation()}>
            <h2 className="clients-modal-title">Delete client</h2>
            <p>Are you sure you want to delete <strong>{deleteConfirm.name || 'this client'}</strong>? This cannot be undone.</p>
            <div className="clients-modal-actions">
              <button type="button" className="clients-btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button type="button" className="clients-btn-danger" onClick={confirmDelete} disabled={saving}>
                {saving ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
